import {
  QuestionCategories,
  QuestionInputTypes,
  QuestionResponse,
  QuestionResponseSubCategories,
  QuestionTypes,
} from "../../interface/QuestionInterface";

export type FeedbackPage =
  | { type: "rating"; subcategory: QuestionResponseSubCategories }
  // category null is the trailing page for paragraph questions without a category
  | { type: "comments"; category: QuestionCategories | null }
  | { type: "company" };

/**
 * Rater page order. Must match QuestionSubCategoryToCategory in the backend
 * (question.model.ts), which orders first-empty-response the same way.
 */
export const CATEGORY_SUBCATEGORIES: [
  QuestionCategories,
  QuestionResponseSubCategories[],
][] = [
  [
    QuestionCategories.Lead,
    [
      QuestionResponseSubCategories.SetDirection,
      QuestionResponseSubCategories.Align,
      QuestionResponseSubCategories.Motivate,
      QuestionResponseSubCategories.Manage,
    ],
  ],
  [
    QuestionCategories.Develop,
    [
      QuestionResponseSubCategories.Discover,
      QuestionResponseSubCategories.Teach,
      QuestionResponseSubCategories.Model,
      QuestionResponseSubCategories.Coach,
    ],
  ],
  [
    QuestionCategories.Care,
    [
      QuestionResponseSubCategories.Know,
      QuestionResponseSubCategories.Connect,
      QuestionResponseSubCategories.Provide,
      QuestionResponseSubCategories.Protect,
    ],
  ],
  [
    QuestionCategories.EQSelf,
    [
      QuestionResponseSubCategories.SelfAwareness,
      QuestionResponseSubCategories.SelfManagement,
      QuestionResponseSubCategories.Motivation,
    ],
  ],
  [
    QuestionCategories.EQOthers,
    [
      QuestionResponseSubCategories.SocialAwareness,
      QuestionResponseSubCategories.RelationalManagement,
    ],
  ],
];

export function categoryOfSubcategory(
  subcategory: QuestionResponseSubCategories,
): QuestionCategories | undefined {
  return CATEGORY_SUBCATEGORIES.find(([, subcategories]) =>
    subcategories.includes(subcategory),
  )?.[0];
}

export function isCompanyResponse(response: QuestionResponse): boolean {
  return response.question.question_type === QuestionTypes.Company;
}

export function isParagraphResponse(response: QuestionResponse): boolean {
  return response.question.input_type === QuestionInputTypes.Paragraph;
}

/**
 * Subcategory as the response enum, which also includes Custom
 */
function responseSubcategory(
  response: QuestionResponse,
): QuestionResponseSubCategories | null {
  return response.question
    .question_subcategory as QuestionResponseSubCategories | null;
}

/**
 * Category of a paragraph question; legacy questions store "" instead of null
 */
export function paragraphCategory(
  response: QuestionResponse,
): QuestionCategories | null {
  return response.question.question_category || null;
}

/**
 * Builds the ordered list of pages the rater walks through, leaving out pages
 * that would have no questions.
 */
export function buildPages(responses: QuestionResponse[]): FeedbackPage[] {
  const standard = responses.filter((item) => !isCompanyResponse(item));
  const ratings = standard.filter((item) => !isParagraphResponse(item));
  const paragraphs = standard.filter(isParagraphResponse);
  const pages: FeedbackPage[] = [];

  for (const [category, subcategories] of CATEGORY_SUBCATEGORIES) {
    for (const subcategory of subcategories) {
      if (ratings.some((item) => responseSubcategory(item) === subcategory)) {
        pages.push({ type: "rating", subcategory });
      }
    }
    if (paragraphs.some((item) => paragraphCategory(item) === category)) {
      pages.push({ type: "comments", category });
    }
  }
  if (responses.some(isCompanyResponse)) {
    pages.push({ type: "company" });
  }
  if (paragraphs.some((item) => paragraphCategory(item) === null)) {
    pages.push({ type: "comments", category: null });
  }
  return pages;
}

/**
 * Index of the page that holds the given response, used to resume the rater
 */
export function pageIndexFor(
  response: QuestionResponse | undefined,
  pages: FeedbackPage[],
): number {
  if (!response?.question) {
    return 0;
  }
  const index = pages.findIndex((page) => {
    if (isCompanyResponse(response)) {
      return page.type === "company";
    }
    if (isParagraphResponse(response)) {
      return (
        page.type === "comments" &&
        page.category === paragraphCategory(response)
      );
    }
    return (
      page.type === "rating" &&
      page.subcategory === responseSubcategory(response)
    );
  });
  return Math.max(index, 0);
}
